import { View, Image, TouchableOpacity, Platform, ActivityIndicator } from 'react-native';
import { Text } from '../../components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Toast from 'react-native-root-toast';

import { deepEqual, formatCurrency, formatParlevelAndQuantity, valueExists } from '../../../common/utils/utils';
import { getProductImageUrl } from '../../../common/utils/products/productImageUrlResolver';
import { useUserContext } from '../../../common/utils/context';
import { CHANGE_TYPES } from '../../../common/constants';
import usePickListItemAction from '../../hooks/usePickListItemAction';
import { useThemedStyles } from '../../hooks/useThemedStyles';

import { Toggle, ErrorCallout, WarningCallout } from '../../components';

import { ProductHealthCompliance, ProductMeasurementUnit } from '../product';

import PlanogramItemSalesLastXDays from './PlanogramItemSalesLastXDays';
import ConfirmStockLevelsButtons from './ConfirmStockLevelsButtons';
import ConfirmValuesButtons from './ConfirmValuesButtons';
import ExpiryDateButtons from './ExpiryDateButtons';
import NoStockOnShelfAction from './NoStockOnShelfAction';
import { createStyles } from './PlanogramItem.styles';
import { getPlanogramItemExpiryInfo } from './PlanogramItemHelpers';

const PlanogramItem = ({
	item, 					// A planogram item
	hideImages,				// From parent state
	hideHealthCompliance,
	readOnly,
	isConfirmStockLevels,
	isConfirmValues,
	pickListItemToggleIsPicked,
	setPickListItemIsPickedBulk,
	isBatched,
	onClickEdit,
	onAddMissingQuantity,	// Function to directly add missing quantity
	showAdvancedView,
	showSalesLastXDays,
	showSalesLastXDaysForPicklists,
	showPickOrderForPicklists,
	isMarket,
	showQuantitySoldAfterRecalc = true, // Default to true if not provided
	showExpiryDates = false, // Default to false for pick lists
	showExpiryDateButtons,
	serviceRunPlanned,
	serviceFrequencyDays,
}) => {
	const { pendingItemIds, pickError, runPickAction } = usePickListItemAction();
	const isSavingPick = pendingItemIds.length > 0;
	const [itemPicked, setItemPicked] = useState(item.isPicked);
	const [itemTotalPickedQuantity, setItemTotalPickedQuantity] = useState((item.pickedQuantity || 0) + (item.pickedExtraQuantity || 0));

	const { tenantSettings, tenant } = useUserContext();

	const {
		selection,
		quantity,
		parlevel,
		capacity,
		price: rawPrice,
		reportedPrice: rawReportedPrice,
		requiredQuantity, // Will be null when setting inventory
		inactive,
	} = item;
	const isItemFullyPicked = Boolean(requiredQuantity && itemTotalPickedQuantity === requiredQuantity);
	const reportedPrice = formatCurrency(rawReportedPrice);
	// Prices and pricesEqual are a bit more involved,
	// in order to handle batching of products.
	let price;
	let pricesEqual;
	if (Array.isArray(rawPrice)) {
		// If this is an array, price is a batched price, so format each price.
		// (Note: it would be odd for equivalent products to be priced differently,
		// this would normally indicate user error.)
		price = rawPrice.map(i => {
			return formatCurrency(i);
		});

		// Simply set pricesEqual to true when batching products. Handling this is a bit trickier,
		// we can do it in the future if required. Simplying viewing prices non-batched
		// to see when reported price !=== VMS price is sufficient.
		pricesEqual = true;
	}
	else {
		price = formatCurrency(rawPrice);
		pricesEqual = rawPrice === rawReportedPrice || rawReportedPrice === null;
	}

	// Join queued prices if necessary
	let queuedPrice;
	if (Array.isArray(item.queuedPrice)) {
		// If this is an array, price is a batched price, so format each price.
		// (Note: it would be odd for equivalent products to be priced differently,
		// this would normally indicate user error.)
		queuedPrice = item.queuedPrice.map(i => {
			return formatCurrency(i);
		});
	}
	else {
		queuedPrice = formatCurrency(item.queuedPrice);
	}

	// Set the product name to include a removed product, if a removed product is present.
	const productName = item.product && item.product?.name;
	const removedProductName = item.removedProduct && item.removedProduct?.name;


	const onTogglePicked = useCallback(async () => {

		const updatedItem = await runPickAction(item.id, async () => {
			let updatedItem = null;
			if (isBatched && setPickListItemIsPickedBulk) {
				updatedItem = await setPickListItemIsPickedBulk(item.product?.id, !itemPicked || (itemTotalPickedQuantity > 0 && itemTotalPickedQuantity < item.requiredQuantity));
			}
			else if (pickListItemToggleIsPicked) {
				updatedItem = await pickListItemToggleIsPicked(item.id);
			}
			return updatedItem;
		});
		if (!updatedItem) {
			return;
		}
		setItemPicked(updatedItem.pickedQuantity + updatedItem.pickedExtraQuantity === item.requiredQuantity);
		setItemTotalPickedQuantity(updatedItem.pickedQuantity + updatedItem.pickedExtraQuantity);

	}, [isBatched, setPickListItemIsPickedBulk, pickListItemToggleIsPicked, item, itemPicked, itemTotalPickedQuantity, runPickAction]);

	const resolvedOnClickEdit = useMemo(() => (onClickEdit ? onClickEdit(item.id) : undefined), [item.id, onClickEdit]);

	const handleAddMissingQuantity = useCallback(() => {
		// Calculate how much was sold after recalculation
		const additionalNeeded = (item.parlevel || 0) - (item.quantity || 0) - item.requiredQuantity;
		if (onAddMissingQuantity && additionalNeeded > 0) {
			// Update the required quantity to include the quantity sold after recalculation
			const newRequiredQuantity = item.requiredQuantity + additionalNeeded;
			onAddMissingQuantity(item.id, newRequiredQuantity);
		} else {
			// Fall back to opening the edit dialog
			resolvedOnClickEdit && resolvedOnClickEdit();
		}
	}, [onAddMissingQuantity, item.requiredQuantity, item.parlevel, item.quantity, item.id, resolvedOnClickEdit]);

	// Helper function to generate explanatory text
	const getMissingQuantityText = useCallback(() => {
		const additionalNeeded = (item.parlevel || 0) - (item.quantity || 0) - item.requiredQuantity;
		if (additionalNeeded > 0) {
			return `${additionalNeeded} sold after pick`;
		}
		return '';
	}, [item.parlevel, item.quantity, item.requiredQuantity]);

	const shouldAutoShowPickedExpiryDates = !isConfirmStockLevels && !isConfirmValues && itemTotalPickedQuantity > 0;
	const shouldShowExpiryDates = showExpiryDates || shouldAutoShowPickedExpiryDates;
	const shouldShowExpiryWarnings = showExpiryDates;
	const useSingleExpiryDateForMarket = isMarket && (isConfirmStockLevels || isConfirmValues);
	const useBatchedExpiryDates = isBatched && !isConfirmStockLevels && !isConfirmValues;
	const resolvedExpiryDates = useBatchedExpiryDates && item.batchedExpiryDates
		? item.batchedExpiryDates
		: item.expiryDates;
	const isPickedWarehouseExpiryDates = Boolean(item.expiryDatesArePickedWarehouseStock);
	const expiryDatesDisplayLabel = isPickedWarehouseExpiryDates
		? 'Picked warehouse expiry'
		: 'Current expiry dates';
	const baseExpiryDatesQuantity = useSingleExpiryDateForMarket
		? 1
		: isPickedWarehouseExpiryDates
			? itemTotalPickedQuantity || 0
			: Math.max(itemTotalPickedQuantity || 0, item.quantity || 0);
	const expiryDatesQuantity = useBatchedExpiryDates && item.batchedExpiryDatesQuantity
		? item.batchedExpiryDatesQuantity
		: baseExpiryDatesQuantity;

	// Calculate expiry dates display and warnings using existing logic
	const { expiryDatesDisplay, mandatoryWarningText, optionalWarningText, expiresBeforeNextServiceText, expiresBeforeNextServiceAdditionalText, expiresBeforeNextServiceDetailsText } = useMemo(() => getPlanogramItemExpiryInfo({
		shouldShowExpiryDates,
		isConfirmStockLevels,
		isConfirmValues,
		resolvedExpiryDates,
		expiryDatesQuantity,
		useSingleExpiryDateForMarket,
		shouldShowExpiryWarnings,
		tenantSettings,
		tenantTimezone: tenant?.timezone,
		serviceRunPlanned,
		serviceFrequencyDays,
		product: item.product,
		expiryDatesDisplayLabel,
		isPickedWarehouseExpiryDates,
	}), [shouldShowExpiryDates, isConfirmStockLevels, isConfirmValues, resolvedExpiryDates, expiryDatesQuantity, useSingleExpiryDateForMarket, shouldShowExpiryWarnings, tenantSettings, tenant?.timezone, serviceRunPlanned, serviceFrequencyDays, item.product, expiryDatesDisplayLabel, isPickedWarehouseExpiryDates]);

	const showExpiresBeforeNextServiceDetails = useCallback(() => {
		if (!expiresBeforeNextServiceDetailsText) {
			return;
		}

		Toast.show(expiresBeforeNextServiceDetailsText, {
			duration: Toast.durations.LONG,
		});
	}, [expiresBeforeNextServiceDetailsText]);

	const styles = useThemedStyles(createStyles);

	useEffect(() => {
		setItemPicked(item.isPicked);
		setItemTotalPickedQuantity((item.pickedQuantity || 0) + (item.pickedExtraQuantity || 0));

	}, [isBatched, item.isPicked, item.pickedQuantity, item.pickedExtraQuantity, setItemTotalPickedQuantity, setItemPicked]);

	const isPickRow = !readOnly && !isConfirmStockLevels && !isConfirmValues;
	// Without a pick callback (view-only pick list) the tick only shows the status.
	const canTogglePicked = Boolean((isBatched && setPickListItemIsPickedBulk) || pickListItemToggleIsPicked);
	// Counters sit under the product (full width) on confirm screens; pick
	// rows keep the quantity and tick box on the right.
	const isStacked = (showAdvancedView && !isConfirmValues) || isConfirmValues;
	const pickedQuantityLeft = itemTotalPickedQuantity > 0 && itemTotalPickedQuantity < item.requiredQuantity;
	const missingQuantityText = isItemFullyPicked ? getMissingQuantityText() : '';
	const showPickFooter = isPickRow && (
		pickedQuantityLeft ||
		(showQuantitySoldAfterRecalc && missingQuantityText) ||
		(shouldShowExpiryDates && expiryDatesDisplay)
	);

	return (
		<View style={[styles.row, inactive && styles.rowInactive]}>
			<TouchableOpacity
				// allow to click entire row in pick list and confirmation stock lvl
				disabled={!resolvedOnClickEdit || isSavingPick}
				onPress={resolvedOnClickEdit}
				style={[styles.rowMain, isStacked && styles.rowMainStacked]}
			>
				<View style={styles.info}>
					{
						!isMarket && (
							<View style={styles.slot}>
								{Array.isArray(selection)
									? selection.map(s => (
										<Text key={s} style={styles.slotText}>{s}</Text>
									))
									: <Text style={styles.slotText}>{selection.substring(0, 3)}</Text>
								}
							</View>
						)
					}

					{
						!hideImages && (
							<View style={styles.imageColumn}>
								{
									Boolean(item.product) && (
										<Image
											style={styles.productImg}
											source={{ uri: getProductImageUrl(item.product) }}
											// resizeMethod='resize' on iphone interferes with building app (probably)
											resizeMethod={Platform.OS === 'ios' ? undefined : 'resize'}
										/>
									)
								}
							</View>
						)
					}

					<View style={styles.details}>
						<View>
							<Text style={styles.productName}>
								{ productName || 'No product' }
							</Text>
							{ removedProductName && isConfirmStockLevels && (
								<Text style={styles.removedProductName}>
									(Replaced {removedProductName})
								</Text>
							)}
						</View>
						<View style={styles.boxes}>
							{
								!item.isIngredient && (
									<>
										<Text style={[styles.box, !pricesEqual && styles.boxPriceMismatch]}>
											{
												// If it's a batched array of prices, join them
												Array.isArray(price) ? price.join(', ') : price
											}
											{
												// If reported price does not equal the VMS price
												!pricesEqual && ` (Reported: ${reportedPrice})`
											}
										</Text>
										{
											item.queuedPrice !== null && item.queuedPriceType === CHANGE_TYPES.NEXT_SERVICE && (
												<Text style={[styles.box, styles.boxQueuedPrice]}>
													{
														// If it's a batched array of prices, join them
														`(Change: ${Array.isArray(queuedPrice) ? queuedPrice.join(', ') : queuedPrice})`
													}
												</Text>
											)
										}
									</>
								)
							}

							<Text style={styles.box}>
								{formatParlevelAndQuantity(quantity, item.product)}/{formatParlevelAndQuantity(parlevel, item.product)}
								{tenantSettings.enablePlanogramItemCapacity ? `/${formatParlevelAndQuantity(capacity, item.product)}` : null}
								<ProductMeasurementUnit product={item.product} />
							</Text>
							{
								!item.isIngredient && (showSalesLastXDays && (isConfirmStockLevels || isConfirmValues) || showSalesLastXDaysForPicklists && !(isConfirmStockLevels || isConfirmValues)) && (
									<PlanogramItemSalesLastXDays
										salesLast365Days={isBatched && item.batchedSalesLast365ays || item.planogramItemStatistics?.salesLast365Days}
										salesLast30Days={isBatched && item.batchedSalesLast30Days || item.planogramItemStatistics?.salesLast30Days}
										salesLast14Days={isBatched && item.batchedSalesLast14Days || item.planogramItemStatistics?.salesLast14Days}
										salesLast7Days={isBatched && item.batchedSalesLast7Days || item.planogramItemStatistics?.salesLast7Days}
										created={item.created}
										lastProductChangeTime={item.planogramItemStatistics?.lastProductChangeTime}
										confirmStockLevelsStyle={true}
									/>
								)
							}
							{
								!hideHealthCompliance && !item.isIngredient && (
									<ProductHealthCompliance product={item.product} />
								)
							}
							{
								valueExists(item.pickOrder) && showPickOrderForPicklists && (
									<Text style={styles.box}>
										{item.pickOrder}
									</Text>
								)
							}
						</View>
					</View>
				</View>

				{
					isPickRow && (
						<View style={styles.pickControls}>
							<TouchableOpacity
								disabled={!resolvedOnClickEdit || isSavingPick}
								onPress={resolvedOnClickEdit}
								accessibilityLabel={`Pick ${formatParlevelAndQuantity(requiredQuantity, item.product)}`}
								style={styles.requiredButton}
							>
								<Text style={[styles.required, Boolean(item.extraQuantity) && styles.extraQuantity]}>
									{formatParlevelAndQuantity(requiredQuantity, item.product)}
								</Text>
								<ProductMeasurementUnit product={item.product} />
							</TouchableOpacity>
							{
								// Quantity sold after recalculation button - shows amount sold after pick was calculated
								// Shows when: setting is enabled AND item is picked AND current quantity + pick quantity < parlevel
								// Only show for picked items
								Boolean(showQuantitySoldAfterRecalc && isItemFullyPicked && ((item.quantity || 0) + item.requiredQuantity) < (item.parlevel || 0)) && (
									<TouchableOpacity
										style={styles.missingQuantityButton}
										onPress={handleAddMissingQuantity}
										disabled={isSavingPick}
										accessibilityRole="button"
										accessibilityLabel={`Add ${(item.parlevel || 0) - (item.quantity || 0) - item.requiredQuantity} sold after pick`}
									>
										<Text style={styles.missingQuantityText}>
											+{(item.parlevel || 0) - (item.quantity || 0) - item.requiredQuantity}
										</Text>
									</TouchableOpacity>
								)
							}
							{isSavingPick ? (
								<ActivityIndicator style={{ width: 48, height: 48 }} accessibilityLabel="Saving picked status" />
							) : (
								<Toggle
									key={`${item.id}_${itemTotalPickedQuantity}`}
									disabled={requiredQuantity === 0}
									readOnly={!canTogglePicked}
									enabled={requiredQuantity !== 0 && ((itemTotalPickedQuantity) === requiredQuantity)}
									partiallyEnabled={(itemTotalPickedQuantity) > 0 && (itemTotalPickedQuantity) < requiredQuantity}
									onChange={onTogglePicked}
								/>
							)}
						</View>
					)
				}
				{
					isConfirmStockLevels && (
						<View style={isStacked ? styles.actionsStacked : styles.actionsSide}>
							<ConfirmStockLevelsButtons
								planogramItem={item}
								showAdvancedView={showAdvancedView}
							/>
						</View>
					)
				}
				{
					isConfirmValues && (
						<View style={styles.actionsStacked}>
							<ConfirmValuesButtons
								planogramItem={item}
							/>
						</View>
					)
				}
			</TouchableOpacity>

			{isConfirmStockLevels && <NoStockOnShelfAction planogramItem={item} />}

			{
				((isConfirmStockLevels || isConfirmValues) && tenantSettings.useExpiryDates) && (
					<ExpiryDateButtons
						planogramItem={item}
						showAdvancedView={showAdvancedView}
						showExpiryDateButtons={showExpiryDateButtons}
						forceShowButtons={isConfirmValues}
					/>
				)
			}

			{
				showPickFooter && (
					<View style={styles.footer}>
						{pickedQuantityLeft && (
							<Text style={styles.footerText}>{ `${itemTotalPickedQuantity} picked. ${item.requiredQuantity - itemTotalPickedQuantity} left.` }</Text>
						)}
						{showQuantitySoldAfterRecalc && Boolean(missingQuantityText) && (
							<Text style={styles.missingQuantityExplanation}>{missingQuantityText}</Text>
						)}
						{shouldShowExpiryDates && expiryDatesDisplay && (
							<View style={styles.expiryDatesContainer}>
								<Text style={styles.footerText}>
									{expiryDatesDisplay}
								</Text>
								{shouldShowExpiryWarnings && mandatoryWarningText && (
									<ErrorCallout
										text={mandatoryWarningText}
									/>
								)}
								{shouldShowExpiryWarnings && expiresBeforeNextServiceText && (
									<TouchableOpacity
										onPress={showExpiresBeforeNextServiceDetails}
										activeOpacity={0.7}
										accessibilityRole="button"
									>
										<ErrorCallout
											text={expiresBeforeNextServiceText}
											additionalText={expiresBeforeNextServiceAdditionalText}
											rightTextIcon="arrowRight"
										/>
									</TouchableOpacity>
								)}
								{shouldShowExpiryWarnings && optionalWarningText && (
									<WarningCallout
										text={optionalWarningText}
									/>
								)}
							</View>
						)}
					</View>
				)
			}
			{isSavingPick && (
				<View style={styles.footer}>
					<Text style={styles.footerText}>Saving picked status…</Text>
				</View>
			)}
			{pickError && <ErrorCallout text={pickError} />}
		</View>
	);
};

export default React.memo(PlanogramItem, (prevProps, currentProps) => deepEqual(prevProps, currentProps));
